/**
 * Plan price normalization helpers (pricing display fix, 2026-08-28).
 *
 * Raw plan amounts reach the UI in several shapes: numbers with binary float
 * noise (49.999999999), numeric strings ("50.00"), and cents values that must
 * be divided by 100 before display. Rendering them directly produced raw
 * values like "50.00" (no currency symbol, wrong rounding) on /pricing and
 * the upgrade banners. All plan-price display must go through
 * `formatPlanPrice` so output is always a canonical "$50.00" string.
 */

export interface PlanPriceOptions {
  /** Value is expressed in cents; divide by 100 before display. */
  cents?: boolean;
  /** Append the per-unit suffix ("/u") used by per-unit condo brackets. */
  perUnit?: boolean;
}

/**
 * Normalize a raw plan amount to a clean dollar number (2-decimal rounded),
 * or null when the value is missing/non-numeric.
 */
export function normalizePlanAmount(value: unknown, opts?: PlanPriceOptions): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n =
    typeof value === 'number'
      ? value
      : Number(String(value).replace(/[^0-9.\-]/g, ''));
  if (!Number.isFinite(n)) return null;
  const dollars = opts?.cents ? n / 100 : n;
  // Kill binary float noise (e.g. 49.999999999 -> 50) without drifting cents.
  return Math.round(dollars * 100) / 100;
}

/**
 * Format a raw plan amount for display: always "$X.XX" (optionally "/u"),
 * never a raw float or numeric string.
 */
export function formatPlanPrice(value: unknown, opts?: PlanPriceOptions): string {
  const n = normalizePlanAmount(value, opts);
  if (n === null) return '—';
  return `$${n.toFixed(2)}${opts?.perUnit ? '/u' : ''}`;
}
